import React, { useMemo, useState, useCallback } from "react";

import { motion, AnimatePresence } from "motion/react";

import { parseGid } from "@shopify/hydrogen-react";

import { useInView } from "react-intersection-observer";

import { useProduct } from "./useProduct";

import { AddToCartButton } from "./AddToCartButton";

import { VariantSelector } from "./VariantSelector";

import { FindInStoreSheet } from "./FindInStoreSheet";

import { FootbedButton } from "./FootbedButton";

import { FeaturesButton } from "./FeaturesButton";

import { SizeButton } from "./SizeButton";

import { TrueFitWidget } from "./TrueFitWidget";

import { BackInStockSheet } from "./BackInStockSheet";

import { FitkitButton } from "./FitkitButton";

import type { ServerDataSuccess } from "./route";

import { Callout, ShopifyRichText } from "@bared/ui";

import { useBetweenTime } from "~/hooks/useTime";

export interface AddToCartStackProps {
  /** Callout content */
  callout?: string | TrustedHTML;
  /** Locale to use for the TrueFit widget */
  locale?: string;
  /** Country code to use for the store availability */
  countryCode?: string;
  /** Enable the floating "add to cart" bar when button not in view */
  enableFloatingBar: boolean;
  /** Is fit kit included for the product */
  isFitKitIncluded: ServerDataSuccess["isFitKitIncluded"];
  /** Gender of the product */
  gender: "Mens" | "Womens" | null;
  /** Enable TrueFit for the product */
  enableTrueFit: boolean;
  /** Enable find in store for the product */
  enableFindInStore: boolean;
  /** Site handle used for special promotions callout*/
  siteHandle: string;
  /** Sizing advice content */
  sizingAdvice?: string | TrustedHTML;
}

export function AddToCartStack({
  callout,
  countryCode = "AU",
  enableFloatingBar = false,
  enableTrueFit = false,
  enableFindInStore = false,
  gender,
  isFitKitIncluded,
  locale = "en-AU",
  siteHandle = "au",
  sizingAdvice,
}: AddToCartStackProps) {
  const { productVariants, shopifyProduct, outOfStockMessageHtml } =
    useProduct();

  const [selectedVariantId, setSelectedVariantId] = useState<string | null>(
    1 === productVariants.length ? productVariants[0].id : null,
  );

  const selectedVariant = useMemo(() => {
    if (!selectedVariantId) return null;

    return (
      productVariants.find(
        (productVariant) => productVariant.id === selectedVariantId,
      ) ?? null
    );
  }, [selectedVariantId]);

  const [backInStockIsOpen, setBackInStockIsOpen] = useState(false);
  const onBackInStockOpen = useCallback(() => setBackInStockIsOpen(true), []);
  const [showEmptyVariantError, setShowEmptyVariantError] = useState(false);
  const [shakeKey, setShakeKey] = useState(0);
  const [showFloatingBar, setShowFloatingBar] = useState(false);

  const setVariantSelectorError = useCallback(() => {
    if (!selectedVariant) {
      setShowEmptyVariantError(true);
      setShakeKey((prev) => prev + 1);
    } else {
      setShowEmptyVariantError(false);
    }
  }, [selectedVariant]);

  // Dynamically fetch the header height.
  const headerHeight =
    "undefined" !== typeof window
      ? getComputedStyle(document.documentElement)
          .getPropertyValue("--header-height")
          .trim()
      : "96px";

  // Determine if the primary "Add to cart" button is in view.
  const { ref } = useInView({
    /**
     * Shrink the root so that the target is treated as "gone" when it goes under
     * the fixed header, rather than out of the viewport.
     */
    rootMargin: `-${headerHeight} 0px 0px 0px`,
    // Update floating bar state.
    onChange(inView) {
      setShowFloatingBar(
        // Primary "Add to cart" is in view, don't show.
        inView
          ? false
          : // Primary "Add to cart" not in view, but we are at the top, don't show.
            200 > window.scrollY
            ? false
            : // Show the floating bar.
              true,
      );
    },
  });

  const betweenTime = useBetweenTime(
    "2025-01-10T09:00:00+11:00",
    "2025-01-26T23:59:59+11:00",
  );

  // Special promotions callout sometimes shown on products along with the regular one
  const showMensPromo =
    gender === "Mens" &&
    !selectedVariant?.compareAtPrice &&
    ["au", "nz"].includes(siteHandle) &&
    betweenTime;

  return (
    <>
      <div className="flex flex-col gap-mini" ref={ref}>
        {outOfStockMessageHtml && (
          <Callout variant="warning">
            <div dangerouslySetInnerHTML={{ __html: outOfStockMessageHtml }} />
          </Callout>
        )}

        <div className="flex justify-start">
          <TrueFitWidget
            productId={`${parseGid(shopifyProduct?.id).id}`}
            locale={locale}
            enableTrueFit={enableTrueFit}
          />
        </div>

        <div className="flex justify-between">
          {enableFindInStore && (
            <FindInStoreSheet
              selectedVariant={selectedVariant}
              countryCode={countryCode}
              setVariantSelectorError={setVariantSelectorError}
            />
          )}
          {gender === "Mens" || gender === "Womens" ? (
            <SizeButton gender={gender} isFitKitIncluded={isFitKitIncluded} />
          ) : null}
        </div>

        {!selectedVariant && showEmptyVariantError && (
          <div
            key={shakeKey}
            role="alert"
            aria-live="assertive"
            aria-atomic="true"
            className="animate__animated animate__shakeX text-hint text-warning"
          >
            Please select a size.
          </div>
        )}

        {1 < productVariants.length && (
          <VariantSelector
            onChange={setSelectedVariantId}
            productVariants={productVariants}
            selectedVariantId={selectedVariantId}
          />
        )}

        <AddToCartButton
          selectedVariant={selectedVariant}
          onBackInStockOpen={onBackInStockOpen}
        />
        {showMensPromo && (
          <Callout size="large" variant="secondary" className="mb-mini">
            <p>Buy 1 Get 50% Off The 2nd. Discount applied at checkout.</p>
          </Callout>
        )}
        {(!!callout || !!sizingAdvice) && (
          <Callout className="mb-mini" variant="secondary" size="large">
            {!!callout && (
              <ShopifyRichText className="mb-micro" content={callout} />
            )}
            {!!sizingAdvice && <ShopifyRichText content={sizingAdvice} />}
          </Callout>
        )}
        {gender === "Mens" || gender === "Womens" ? (
          <div className="flex flex-col gap-[12px]">
            {isFitKitIncluded ? (
              <FitkitButton
                gender={gender}
                isFitKitIncluded={isFitKitIncluded}
              />
            ) : null}
            <div className="flex flex-row gap-mini">
              <FootbedButton
                gender={gender}
                isFitKitIncluded={isFitKitIncluded}
              />
              <FeaturesButton
                gender={gender}
                productName={shopifyProduct.styleNameMetafield?.value ?? null}
              />
            </div>
          </div>
        ) : null}
      </div>
      {/* When primary "Add to cart" button not in view, show floating bar on mobile */}
      {enableFloatingBar && (
        <AnimatePresence>
          {showFloatingBar && (
            <motion.div
              animate={{ y: 0 }}
              exit={{ y: "-100%" }}
              initial={{ y: "-100%" }}
              transition={{ bounce: 0 }}
              className="fixed left-zero right-zero top-[--header-height] z-20 flex bg-white md:hidden"
            >
              <VariantSelector
                size="md"
                className="flex-1"
                onChange={setSelectedVariantId}
                productVariants={productVariants}
                selectedVariantId={selectedVariantId}
              />
              <AddToCartButton
                size="medium"
                selectedVariant={selectedVariant}
                onBackInStockOpen={onBackInStockOpen}
              />
            </motion.div>
          )}
        </AnimatePresence>
      )}
      {!!selectedVariant && "NOTIFY" === selectedVariant.status && (
        <BackInStockSheet
          active={backInStockIsOpen}
          setActive={setBackInStockIsOpen}
          selectedVariantId={selectedVariantId}
        />
      )}
    </>
  );
}
